/**
 * LoginPage — email + password login with Google fallback. Pairs with
 * SignupPage and ForgotPasswordPage; routes to / on success.
 */

import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { useAuthStore } from '@/stores/authStore';

interface LocationState {
  from?: string;
}

export function LoginPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const location = useLocation();
  const loginWithEmail = useAuthStore((s) => s.loginWithEmail);
  const loginWithGoogle = useAuthStore((s) => s.loginWithGoogle);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const redirectTarget = (location.state as LocationState | null)?.from ?? '/';

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await loginWithEmail({ email, password });
      navigate(redirectTarget, { replace: true });
    } catch (err) {
      const code = err instanceof Error ? err.message : 'generic';
      setError(t(`errors.${code}`, { defaultValue: t('errors.generic') }));
    } finally {
      setSubmitting(false);
    }
  };

  /** Triggers Supabase's OAuth redirect. The browser navigates away
   *  to Google's consent screen and lands back on `/auth/callback`,
   *  where the auth-state subscription populates the store and the
   *  callback page redirects to the original destination. */
  const handleGoogle = async () => {
    setError(null);
    try {
      // We stash the post-login destination so /auth/callback can
      // honour the same redirect logic the email path uses.
      sessionStorage.setItem('gi-engine.post-login-redirect', redirectTarget);
      await loginWithGoogle();
      // signInWithOAuth redirects the page; this line only runs if
      // the redirect failed silently.
    } catch {
      setError(t('errors.google_failed'));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      <div className="text-center">
        <h2 className="text-lg font-semibold tracking-tight">{t('login.heading')}</h2>
        <p className="mt-1 text-sm text-ink-muted">{t('login.subheading')}</p>
      </div>

      <div>
        <label htmlFor="login-email" className="mb-1 block text-sm font-medium text-ink">
          {t('login.email_label')}
        </label>
        <input
          id="login-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          required
          className="block w-full rounded-md border border-border bg-canvas px-3 py-2 text-sm text-ink placeholder:text-ink-subtle focus:border-spark focus:outline-none"
        />
      </div>
      <div>
        <div className="mb-1 flex items-center justify-between">
          <label htmlFor="login-password" className="block text-sm font-medium text-ink">
            {t('login.password_label')}
          </label>
          <Link to="/forgot-password" className="text-xs font-medium text-spark hover:underline">
            {t('login.forgot')}
          </Link>
        </div>
        <input
          id="login-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          required
          className="block w-full rounded-md border border-border bg-canvas px-3 py-2 text-sm text-ink placeholder:text-ink-subtle focus:border-spark focus:outline-none"
        />
      </div>

      {error ? (
        <p role="alert" className="rounded-md bg-danger/10 px-3 py-2 text-sm text-danger">
          {error}
        </p>
      ) : null}

      <Button type="submit" className="w-full" size="lg" disabled={submitting}>
        {t('login.submit')}
      </Button>

      <div className="flex items-center gap-3">
        <span className="h-px flex-1 bg-border" />
        <span className="text-xs uppercase tracking-wider text-ink-subtle">{t('login.or')}</span>
        <span className="h-px flex-1 bg-border" />
      </div>

      <Button
        type="button"
        variant="secondary"
        size="lg"
        className="w-full"
        onClick={handleGoogle}
      >
        {t('login.continue_with_google', { defaultValue: 'Continue with Google' })}
      </Button>

      <p className="text-center text-sm text-ink-muted">
        {t('login.no_account')}{' '}
        <Link to="/signup" className="font-medium text-spark hover:underline">
          {t('login.signup_link')}
        </Link>
      </p>
    </form>
  );
}
